import { useMemo, useState } from 'react';
import { fmtNum } from '../../utils/statsFormat';
import { axisGutter, niceTicks, seriesStyle, useChartTooltip, useElementWidth } from './useChartLayout';
import ChartTooltip from './ChartTooltip';
import './charts.css';

/**
 * Time-series line chart with a crosshair and min/max bands.
 *
 * Replaces PerfChart, which computed ONE y-range across all its series — so
 * "Messages/sec" plotted a rate against a cumulative counter and the counter
 * squashed the rate flat. This chart takes one unit per chart and no second
 * scale, ever: two measures of different scale belong in two charts.
 *
 * Bands: where a sample carries vMin/vMax (the backend emits these for gauge
 * and rate metrics), the low-high spread for that bucket is filled behind the
 * line in the same hue. At the 7-day range a bucket is 30 minutes of
 * one-minute samples, so the band is the honest answer to "how much did this
 * move inside the bucket" that a single averaged point throws away.
 *
 * Props:
 *   series [{ name, slot?, samples: [{ ts, v, vMin?, vMax? }] }]
 *   yMin/yMax  optional fixed bounds (e.g. 0-100 for a percentage)
 *   xFormat    optional x-axis / tooltip time formatter. Defaults to local
 *              HH:MM, which is right for a live feed and wrong for anything
 *              bucketed by day — a daily series rendered as HH:MM shows the
 *              same midnight twice and tells the reader nothing.
 */

/** Float slack for comparing a rounded tick against a domain bound. */
const EPS = 1e-9;

function LineChart({
  series = [],
  height = 240,
  yMin,
  yMax,
  format = (v) => fmtNum(v, 1),
  tickFormat,
  xFormat,
  unit,
  // What the y-axis measures, printed above it ("MAE, min"). Defaults to the unit, so a
  // chart that already says what its values are in labels its scale too.
  axisLabel = unit,
}) {
  const [wrapRef, width] = useElementWidth(560);
  const { tip, show, hide } = useChartTooltip();
  const [hoverTs, setHoverTs] = useState(null);

  const fmtTick = tickFormat || ((v) => fmtNum(v, Math.abs(v) >= 100 ? 0 : 1));

  const model = useMemo(() => {
    const all = series.flatMap(s => (s.samples || []).filter(p => p && p.v != null));
    if (all.length === 0) return null;

    const tsList = [...new Set(all.map(p => p.ts))].sort((a, b) => a - b);
    const tsMin = tsList[0];
    const tsMax = tsList[tsList.length - 1];

    // Bounds must include the band extremes, or the band clips outside the plot.
    let lo = yMin != null ? yMin : Math.min(...all.map(p => (p.vMin != null ? p.vMin : p.v)));
    let hi = yMax != null ? yMax : Math.max(...all.map(p => (p.vMax != null ? p.vMax : p.v)));
    if (hi === lo) hi = lo + 1;

    return { tsList, tsMin, tsMax, lo, hi };
  }, [series, yMin, yMax]);

  if (!model) {
    return <div className="chart-card-empty">No data</div>;
  }

  const { tsList, tsMin, tsMax, lo, hi } = model;
  const fmtX = xFormat || defaultTimeLabel(tsMin, tsMax);

  // The floor is threaded through so a signed series keeps its negative half.
  // Previously the auto branch discarded every tick below zero, which left a
  // bias or offset chart drawing its line against gridlines that did not exist.
  const fixedBounds = yMin != null && yMax != null;
  const ticks = fixedBounds
    ? niceTicks(yMax, 4, yMin)
    : niceTicks(hi, 4, Math.min(0, lo));

  // niceTicks rounds OUTWARDS to a whole step, so it routinely returns ticks
  // beyond the requested bounds — niceTicks(12, 4, -12) gives [-20…20] for a
  // ±12 domain. Those map outside the plot box, and .chart-svg is
  // overflow:visible, so they were painted outside the CARD: the stray "20.0"
  // and "-20.0" floating between the regime chart and its neighbours.
  //
  // With fixed bounds the caller owns the domain, so out-of-range ticks are
  // dropped. With automatic bounds the domain instead grows to the ticks, which
  // is what puts the top gridline on the top of the plot rather than somewhere
  // arbitrary inside it.
  const inRange = ticks.filter(t => t >= lo - EPS && t <= hi + EPS);
  const gridVals = fixedBounds
    ? (inRange.length > 1 ? inRange : [lo, hi])
    : (ticks.length > 1 ? ticks : [lo, hi]);
  const domainLo = fixedBounds ? lo : Math.min(lo, gridVals[0]);
  const domainHi = fixedBounds ? hi : Math.max(hi, gridVals[gridVals.length - 1]);

  const gutter = axisGutter(gridVals, fmtTick);

  // Extra headroom when the axis carries a label, so it never collides with the top tick.
  const pad = { top: axisLabel ? 26 : 12, right: 12, bottom: 26, left: gutter };
  const plotW = Math.max(10, width - pad.left - pad.right);
  const plotH = Math.max(10, height - pad.top - pad.bottom);

  const xOf = ts => pad.left + ((ts - tsMin) / Math.max(1, tsMax - tsMin)) * plotW;
  const yOf = v => pad.top + plotH - ((v - domainLo) / (domainHi - domainLo)) * plotH;

  // One label per ~120px, so a full-width card gets a real axis instead of two
  // lonely endpoints. The endpoints anchor inwards; everything between is
  // centred on its tick.
  const xTickCount = Math.max(2, Math.min(6, Math.floor(plotW / 120)));
  const xTicks = tsMax > tsMin
    ? Array.from({ length: xTickCount }, (_, i) =>
        tsMin + ((tsMax - tsMin) * i) / (xTickCount - 1))
    : [tsMin];

  const onMove = (evt) => {
    const rect = wrapRef.current?.getBoundingClientRect();
    if (!rect) return;
    const px = evt.clientX - rect.left;
    // Snap to the nearest sample so the reader aims at a time, not a 2px line.
    let nearest = tsList[0];
    let best = Infinity;
    for (const ts of tsList) {
      const d = Math.abs(xOf(ts) - px);
      if (d < best) { best = d; nearest = ts; }
    }
    setHoverTs(nearest);

    // One tooltip lists EVERY series at that x — the pointer never has to land
    // on a line to get a value.
    const rows = [];
    for (let i = 0; i < series.length; i++) {
      const s = series[i];
      const p = (s.samples || []).find(q => q && q.ts === nearest && q.v != null);
      if (!p) continue;
      const { color } = seriesStyle(s.slot != null ? s.slot : i + 1);
      const band = (p.vMin != null && p.vMax != null && (p.vMax - p.vMin) > 1e-9)
        ? ` (${format(p.vMin)}–${format(p.vMax)})`
        : '';
      rows.push({ name: s.name + band, value: format(p.v) + (unit ? ` ${unit}` : ''), color });
    }
    if (rows.length === 0) { hide(); return; }
    show({ x: xOf(nearest), y: plotH / 2, title: fmtX(nearest), rows });
  };

  const onLeave = () => { setHoverTs(null); hide(); };

  return (
    <div className="chart-plot" ref={wrapRef}>
      <svg className="chart-svg" width="100%" height={height}
           viewBox={`0 0 ${width} ${height}`} role="img"
           onPointerMove={onMove} onPointerLeave={onLeave}>
        {axisLabel && (
          <text x={pad.left} y={11} className="chart-axis-label chart-axis-title"
                textAnchor="start">{axisLabel}</text>
        )}
        {gridVals.map((t, i) => (
          <g key={`g${i}`}>
            <line x1={pad.left} y1={yOf(t)} x2={width - pad.right} y2={yOf(t)}
                  className="chart-grid-line" />
            <text x={pad.left - 7} y={yOf(t) + 4} className="chart-axis-label"
                  textAnchor="end">{fmtTick(t)}</text>
          </g>
        ))}

        {/* Bands first, so lines draw over their own spread. */}
        {series.map((s, si) => {
          const pts = (s.samples || []).filter(p => p && p.vMin != null && p.vMax != null);
          if (pts.length < 2) return null;
          const { color } = seriesStyle(s.slot != null ? s.slot : si + 1);
          const up = pts.map(p => `${xOf(p.ts).toFixed(1)},${yOf(p.vMax).toFixed(1)}`);
          const down = pts.slice().reverse()
            .map(p => `${xOf(p.ts).toFixed(1)},${yOf(p.vMin).toFixed(1)}`);
          return (
            <polygon key={`band${si}`} points={[...up, ...down].join(' ')}
                     fill={color} className="chart-band-fill" />
          );
        })}

        {series.map((s, si) => {
          const pts = (s.samples || []).filter(p => p && p.v != null);
          if (pts.length === 0) return null;
          const { color, dash } = seriesStyle(s.slot != null ? s.slot : si + 1);
          if (pts.length === 1) {
            return <circle key={`p${si}`} cx={xOf(pts[0].ts)} cy={yOf(pts[0].v)} r="3"
                           fill={color} />;
          }
          return (
            <polyline key={`l${si}`} fill="none" stroke={color} strokeDasharray={dash}
                      className="chart-line"
                      points={pts.map(p => `${xOf(p.ts).toFixed(1)},${yOf(p.v).toFixed(1)}`).join(' ')} />
          );
        })}

        {hoverTs != null && (
          <>
            <line x1={xOf(hoverTs)} y1={pad.top} x2={xOf(hoverTs)} y2={pad.top + plotH}
                  className="chart-crosshair" />
            {series.map((s, si) => {
              const p = (s.samples || []).find(q => q && q.ts === hoverTs && q.v != null);
              if (!p) return null;
              const { color } = seriesStyle(s.slot != null ? s.slot : si + 1);
              return (
                <circle key={`m${si}`} cx={xOf(p.ts)} cy={yOf(p.v)} r="4"
                        fill={color} className="chart-marker" />
              );
            })}
          </>
        )}

        {xTicks.map((ts, i) => (
          <text key={`x${i}`}
                x={xOf(ts)} y={height - 8}
                className="chart-axis-label"
                textAnchor={i === 0 ? 'start' : i === xTicks.length - 1 ? 'end' : 'middle'}>
            {fmtX(ts)}
          </text>
        ))}
      </svg>

      <ChartTooltip tip={tip} containerWidth={width} />
    </div>
  );
}

/** Local-clock HH:MM — operators read their own wall clock for live health. */
function timeLabel(ms) {
  const d = new Date(ms);
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/**
 * Axis label resolution follows the span.
 *
 * HH:MM is right for a live feed and useless for anything bucketed by day: a
 * 30-day series of midnight-stamped points printed "01:00" at both ends of the
 * axis, which told the reader nothing at all. Past a day and a half the label
 * becomes a date. Callers with their own convention pass `xFormat` and bypass
 * this entirely.
 */
function defaultTimeLabel(tsMin, tsMax) {
  if (tsMax - tsMin > 36 * 3600 * 1000) {
    return (ms) => new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short' });
  }
  return timeLabel;
}

export default LineChart;
